import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { api } from 'src/api';
import { livyEvent, resetEngagement } from './LivyGreeting';
import { describeSchedule } from './LivyReportActions';
import { CATEGORIES, reportForTool, reportsFor } from './reportCatalog';
const SECTION = {
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  px: '6px',
  pt: '12px',
  pb: '4px'
};

/**
 * The report center, like QuickBooks': starred reports, the one this person runs most, then every report that applies
 * to this customer by category, each one click; and the reports they asked Livy to email them monthly.
 */
export function LivyReports(_ref) {
  let billingType = _ref.billingType,
    favorites = _ref.favorites,
    mostRun = _ref.mostRun,
    onRun = _ref.onRun;
  const _useState = useState(favorites !== null && favorites !== void 0 ? favorites : []),
    _useState2 = _slicedToArray(_useState, 2),
    stars = _useState2[0],
    setStars = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    scheduled = _useState4[0],
    setScheduled = _useState4[1];
  const reports = reportsFor(billingType);
  const most = reportForTool(mostRun, billingType);

  // Keyed on the prop itself (undefined when absent), never a fresh [] that would re-run every render.
  useEffect(() => setStars(favorites !== null && favorites !== void 0 ? favorites : []), [favorites]);
  useEffect(() => {
    api.Allocations.getScheduledReports().then(resp => {
      var _response$items, _response;
      return setScheduled((_response$items = resp === null || resp === void 0 || (_response = resp.response) === null || _response === void 0 ? void 0 : _response.items) !== null && _response$items !== void 0 ? _response$items : []);
    }).catch(() => setScheduled([]));
  }, []);
  const toggleStar = key => {
    const starred = stars.includes(key);
    setStars(starred ? stars.filter(k => k !== key) : [...stars, key]);
    // Refreshed only once the star is stored, or the refetch could read the old list back.
    void livyEvent(starred ? 'report_unstar' : 'report_star', key).then(resetEngagement);
  };
  const setEnabled = (s, enabled) => {
    setScheduled(list => list.map(x => x.id === s.id ? _objectSpread(_objectSpread({}, x), {}, {
      enabled
    }) : x));
    void api.Allocations.setScheduledReportEnabled(s.id, enabled).catch(() => setScheduled(list => list.map(x => x.id === s.id ? _objectSpread(_objectSpread({}, x), {}, {
      enabled: !enabled
    }) : x)));
  };
  const remove = s => {
    setScheduled(list => list.filter(x => x.id !== s.id));
    void api.Allocations.deleteScheduledReport(s.id).catch(() => setScheduled(list => [...list, s]));
  };
  const card = (r, prefix) => /*#__PURE__*/React.createElement(Stack, {
    key: "".concat(prefix, "-").concat(r.key),
    direction: "row",
    alignItems: "center",
    sx: {
      borderRadius: '10px',
      '&:hover': {
        bgcolor: 'action.hover'
      }
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => onRun(r.prompt),
    sx: {
      flex: 1,
      display: 'block',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      px: '10px',
      py: '7px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, r.title), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary'
    }
  }, r.description)), /*#__PURE__*/React.createElement(Tooltip, {
    title: stars.includes(r.key) ? 'Remove from favorites' : 'Add to favorites'
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    "aria-label": "".concat(stars.includes(r.key) ? 'Unstar' : 'Star', " ").concat(r.title),
    "aria-pressed": stars.includes(r.key),
    onClick: () => toggleStar(r.key),
    sx: {
      mr: '4px',
      color: stars.includes(r.key) ? 'warning.main' : 'text.disabled'
    }
  }, stars.includes(r.key) ? '★' : '☆')));
  const starred = reports.filter(r => stars.includes(r.key));
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      px: '10px',
      pb: '14px'
    },
    "aria-label": "Reports"
  }, starred.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    sx: SECTION
  }, "Favorites"), starred.map(r => card(r, 'fav'))), most && !stars.includes(most.key) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    sx: SECTION
  }, "You run most"), card(most, 'most')), CATEGORIES.map(category => {
    // A report pinned above (starred or run most) is not listed a second time.
    const inCategory = reports.filter(r => r.category === category && !stars.includes(r.key) && r.key !== (most === null || most === void 0 ? void 0 : most.key));
    return inCategory.length === 0 ? null : /*#__PURE__*/React.createElement(React.Fragment, {
      key: category
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: SECTION
    }, category), inCategory.map(r => card(r, category)));
  }), scheduled.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    sx: SECTION
  }, "Emailed monthly"), scheduled.map(s => /*#__PURE__*/React.createElement(Stack, {
    key: s.id,
    direction: "row",
    alignItems: "center",
    spacing: "6px",
    sx: {
      px: '10px',
      py: '4px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      flex: 1
    }
  }, describeSchedule(s).replace(/^the /, '').replace(/^./, c => c.toUpperCase()), s.lastSentOn && /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: {
      color: 'text.secondary',
      fontSize: 12
    }
  }, ' ', "\xB7 last sent ", new Date(s.lastSentOn).toLocaleDateString('en-US'))), /*#__PURE__*/React.createElement(Switch, {
    size: "small",
    checked: s.enabled,
    onChange: e => setEnabled(s, e.target.checked),
    inputProps: {
      'aria-label': "Email ".concat(describeSchedule(s), " monthly")
    }
  }), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => remove(s),
    sx: {
      textTransform: 'none',
      minWidth: 0
    }
  }, "Stop")))));
}